import dayjs from "dayjs";

/** ───────────── Helpers de normalización ───────────── **/
const norm = (v) => String(v ?? '');

// Robust key extractor for attractions/activities
export const getActivityKey = (a) =>
    norm(
        a?.id ??
        a?.activity_id ??
        a?.attraction_id ??
        a?.code ??
        a?.slug ??
        a?.name
    );

const ensureActivityObject = (a) => {
    // Si ya es objeto, sólo normalizamos su id y name
    if (a && typeof a === 'object') {
        const id = getActivityKey(a);
        const name = a.name ?? id;
        return { ...a, id, name };
    }
    // Si es string/number (una key), lo convertimos a objeto estándar
    const id = norm(a);
    return { id, name: id, value: 1 };
};

// Helper: given an extras-like object, return a normalized attractions array.
export const normalizeExtrasToAttractions = (extras) => {
    if (!extras) return [];
    // accept multiple shapes: prefer `selectedAttractions` (options payload), then `attractions`, then legacy `activities`
    const raw = Array.isArray(extras.selectedAttractions)
        ? extras.selectedAttractions
        : (Array.isArray(extras.attractions) ? extras.attractions : (Array.isArray(extras.activities) ? extras.activities : []));
    return raw.map(ensureActivityObject);
};

const paxMatches = (ratePax, paxTitle) => {
    if (!ratePax) return false;
    if (Array.isArray(ratePax)) return ratePax.map(String).includes(String(paxTitle));
    if (typeof ratePax === 'string') return ratePax.split(',').map(s => s.trim()).includes(String(paxTitle));
    return false;
};

/** ───────────── Funciones exportadas existentes ───────────── **/
export const getAssistantsString = (pax) => {
    return pax
        .filter(passenger => Number(passenger.value) > 0) // Ignorar si value es 0
        .map(passenger => `${passenger.value} ${passenger.title}${passenger.value > 1 ? 's' : ''}`)
        .join(', ');
};

// Nota: si tus objetos de pax traen "value" y no "total", quizá quieres usar value aquí.
// Lo dejo como estaba, pero considera cambiar currentValue.total -> currentValue.value.
export const getTotalAssistants = (pax) => {
    return pax.reduce((accumulator, currentValue) => accumulator + (Number(currentValue.total) || 0), 0);
};

/** ───────────── prePrareTourBookingData con IDs robustos ───────────── **/
export const prePrareTourBookingData = (tour, date, items, extras, options = {}) => {
    const getCoverOrDefault = (array, condition) => {
        return array?.find?.(condition) || array?.[0] || null;
    };
    const condition = (image) => image?.isCover;
    const cover = getCoverOrDefault(tour?.images, condition);

    // 1) Fuente de attractions (selectedAttractions de options > extras.selectedAttractions > extras.attractions > extras.activities)
    const activitiesSourceRaw =
        (Array.isArray(options?.selectedAttractions) && options.selectedAttractions.length > 0)
            ? options.selectedAttractions
            : (Array.isArray(extras?.selectedAttractions) && extras.selectedAttractions.length > 0)
                ? extras.selectedAttractions
                : (Array.isArray(extras?.attractions) && extras.attractions.length > 0 ? extras.attractions : (extras?.activities || []));

    // 2) Normalizamos SIEMPRE a objetos con id y name estables (string)
    const activitiesNormalized = (activitiesSourceRaw || []).map(ensureActivityObject);

    // 1b) Availability hours: prefer options, then extras, then tour
    const availabilitySource = options?.availabilityHours ?? extras?.availabilityHours ?? tour?.availabilityHours;
    // Normalize to the same rough shape the UI expects: an array where the first element may be the day's hours array
    let normalizedAvailability = [];
    if (availabilitySource) {
        if (Array.isArray(availabilitySource)) {
            // keep as-is (could already be [hoursArray] or hoursArray)
            normalizedAvailability = availabilitySource;
        } else {
            // wrap single object/structure into an array to preserve compatibility
            normalizedAvailability = [availabilitySource];
        }
    }

    // Helper to pick the first hour string/object available
    const findFirstHour = (avail) => {
        if (!avail || avail.length === 0) return null;
        const firstContainer = Array.isArray(avail[0]) ? avail[0] : avail;
        if (!firstContainer || firstContainer.length === 0) return null;
        const first = firstContainer[0];
        // try to return a primitive hour (e.g. "09:00") or an object with .hour
        return (first && (first.hour || first.time)) ? (first.hour || first.time) : (typeof first === 'string' ? first : null);
    };
    const inferredFirstHour = findFirstHour(normalizedAvailability);

    // 3) Cálculo de total tolerante a estructuras
    const totalPrice = (Array.isArray(items) ? items : []).reduce((total, item) => {
        const itemPrice = Number(item.price) || 0;
        const itemValue = Number(item.value) || 0;
        let itemTotal = itemPrice * itemValue;

        // Suma de extras por pax que aplica
        activitiesNormalized.forEach(activity => {
            const rates = Array.isArray(activity?.rates) ? activity.rates : [];
            rates.forEach(rate => {
                const ratePrice = Number(rate?.price) || 0;
                if (paxMatches(rate?.pax, item?.title)) {
                    itemTotal += ratePrice * itemValue;
                }
            });
        });

        return total + itemTotal;
    }, 0);

    const languages = options.languages ?? tour?.languages ?? [];
    const details = tour?.tourDetails ?? tour;

    const tourtoBook = {
        name: tour?.name,
        slug: tour?.slug,
        city: tour?.city?.slug,
        country: tour?.country?.slug,
        cover: cover?.imagen || cover, // mantiene comportamiento previo
        // ítems (pax) explícitos + legado booking
        items: items,
        booking: items,

        // 4) No guardar attractions dentro de `extras` (migrado a options.selectedAttractions)
        // Mantener compatibilidad poniendo las attractions dentro de options.selectedAttractions
        // También conservar/guardar availabilityHours y seleccionar la primera hora disponible si no viene en options
        options: {
            ...(options || {}),
            selectedAttractions: activitiesNormalized,
            // keep provided availabilityHours if present; otherwise set normalizedAvailability
            availabilityHours: options?.availabilityHours ?? (normalizedAvailability.length > 0 ? normalizedAvailability : undefined),
            // selectedHour: prefer explicit option, else inferred from availability
            selectedHour: options?.selectedHour ?? inferredFirstHour,
        },

        // 5) Guarda tourDetails y alias "details"
        tourDetails: details,
        details: details,

        // idiomas
        languages: languages,

        date: dayjs(date).format('YYYY-MM-DD'),
        total: totalPrice,
        iva: tour?.iva,
    };

    // 6) Superficia customizations / top-level convenience según options
    const customizations = tourtoBook.customizations || {};

    if (options?.selectedCompanion) {
        customizations.travelCompanion = options.selectedCompanion;
        tourtoBook.travelCompanion = options.selectedCompanion;
    }

    if (options?.languages && Array.isArray(options.languages) && options.languages.length > 0) {
        customizations.language = options.languages[0];
        tourtoBook.language = options.languages[0];
    }

    if (options?.selectedGuide) {
        customizations.guide = options.selectedGuide;
        tourtoBook.guide = options.selectedGuide;
    }

    if (options?.selectedVehicle) {
        // conserva vehicle_option.value si venía
        customizations.vehicle = options.selectedVehicle;
        tourtoBook.vehicle = options.selectedVehicle;
    }

    if (options?.selectedAttractions) {
        // guarda también en customizations normalizado para compatibilidad UI (no en extras)
        customizations.attractions = (options.selectedAttractions || []).map(ensureActivityObject);
        tourtoBook.attractions = customizations.attractions;
    }

    if (Object.keys(customizations).length > 0) {
        tourtoBook.customizations = customizations;
    }

    return tourtoBook;
};

export const getTourSelectedAttractions = (tour = {}) => {
    const list =
        tour?.options?.selectedAttractions ??
        tour?.customizations?.attractions ??
        tour?.extras?.selectedAttractions ??
        tour?.extras?.attractions ??
        tour?.extras?.activities ??
        [];
    return normalizeExtrasToAttractions({ selectedAttractions: list });
};

export const getTourSelectedHour = (tour = {}) => (
    tour?.options?.selectedHour ??
    tour?.customizations?.schedule?.selectedHour ??
    tour?.customizations?.schedule ??
    tour?.selectedHour ??
    null
);

export const getTourPricedOptions = (tour = {}) => (
    Array.isArray(tour?.pricedOptions)
        ? tour.pricedOptions
        : (Array.isArray(tour?.customizations?.pricedOptions) ? tour.customizations.pricedOptions : [])
);

export const normalizeCartTour = (tour = {}) => {
    const selectedAttractions = getTourSelectedAttractions(tour);
    const selectedHour = getTourSelectedHour(tour);
    const pricedOptions = getTourPricedOptions(tour);
    const availabilityHours = tour?.options?.availabilityHours ?? tour?.availabilityHours;

    const options = {
        ...(tour.options || {}),
        selectedAttractions,
        availabilityHours,
        ...(selectedHour ? { selectedHour } : {}),
    };

    const customizations = {
        ...(tour.customizations || {}),
        attractions: selectedAttractions,
        pricedOptions,
        ...(selectedHour ? { schedule: { selectedHour } } : {}),
    };

    return {
        ...tour,
        code: tour?.code ?? tour?.tourDetails?.code ?? tour?.details?.code ?? null,
        options,
        customizations,
        pricedOptions,
        selectedHour: selectedHour ?? undefined,
        extras: {
            ...(tour.extras || {}),
            selectedAttractions,
        },
    };
};

const getActivityRatePriceForPax = (activity, paxType) => {
    const rates = Array.isArray(activity?.rates) ? activity.rates : [];
    const found = rates.find((r) => paxMatches(r?.pax, paxType));
    return Number(found?.price) || 0;
};

export const getTourPricingBreakdown = (tour = {}) => {
    const normalized = normalizeCartTour(tour);
    const ivaRate = Number(normalized?.iva) || 0;

    const pax = (Array.isArray(normalized?.booking) ? normalized.booking : [])
        .filter((item) => (Number(item?.value) || 0) > 0)
        .map((item) => {
            const quantity = Number(item?.value) || 0;
            const unitPrice = Number(item?.price) || 0;
            return {
                pax_type: item?.title,
                quantity,
                unit_price: unitPrice,
                total: unitPrice * quantity,
            };
        });

    const paxSubtotal = pax.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);

    const activities = getTourSelectedAttractions(normalized).map((activity) => {
        const byPax = pax
            .map((paxItem) => {
                const unitPrice = getActivityRatePriceForPax(activity, paxItem.pax_type);
                if (unitPrice <= 0 || paxItem.quantity <= 0) return null;
                return {
                    pax_type: paxItem.pax_type,
                    quantity: paxItem.quantity,
                    unit_price: unitPrice,
                    total: unitPrice * paxItem.quantity,
                };
            })
            .filter(Boolean);

        const totalFromRates = byPax.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);
        const fallbackPrice = Number(activity?.price) || 0;

        return {
            id: activity?.id ?? activity?.code ?? activity?.slug ?? activity?.name ?? null,
            name: activity?.name ?? activity?.label ?? activity?.title ?? null,
            code: activity?.code ?? null,
            by_pax: byPax,
            total: totalFromRates > 0 ? totalFromRates : fallbackPrice,
        };
    }).filter((activity) => (Number(activity?.total) || 0) > 0);

    const activitiesSubtotal = activities.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);

    const extras = getTourPricedOptions(normalized)
        .filter((opt) => String(opt?.source || opt?.type || '').toLowerCase() !== 'attraction')
        .map((opt) => ({
            source: opt?.source || opt?.type || 'extra',
            label: opt?.label || opt?.name || opt?.title || 'Extra',
            price: Number(opt?.price) || 0,
        }))
        .filter((opt) => opt.price > 0);

    const extrasOptionsSubtotal = extras.reduce((acc, item) => acc + (Number(item?.price) || 0), 0);
    const extrasSubtotal = activitiesSubtotal + extrasOptionsSubtotal;
    const subtotal = paxSubtotal + extrasSubtotal;
    const ivaTotal = ivaRate > 0 ? Math.ceil(subtotal * (ivaRate / 100)) : 0;
    const total = subtotal + ivaTotal;

    return {
        pax,
        activities,
        extras,
        totals: {
            pax_subtotal: paxSubtotal,
            extras_subtotal: extrasSubtotal,
            subtotal,
            iva_rate: ivaRate,
            iva_total: ivaTotal,
            total,
        },
    };
};

export const buildBookingPayloadFromCart = (tours = [], lead = {}) => {
    const leadPayload = {
        email: lead?.email,
        name: lead?.name,
        last_name: lead?.lastName,
        phone: lead?.phone,
    };

    const normalizedTours = (Array.isArray(tours) ? tours : []).map((tour) => normalizeCartTour(tour));
    const toursPayload = normalizedTours.map((tour) => {
        const breakdown = getTourPricingBreakdown(tour);
        return {
            name: tour?.name ?? null,
            code: tour?.code ?? tour?.tourDetails?.code ?? tour?.details?.code ?? null,
            slug: tour?.slug ?? null,
            travel_date: tour?.date ?? null,
            selected_hour: getTourSelectedHour(tour),
            lead: leadPayload,
            pax: breakdown.pax,
            activities: breakdown.activities,
            extras: breakdown.extras,
            totals: breakdown.totals,
        };
    });

    const orderTotal = toursPayload.reduce((acc, item) => acc + (Number(item?.totals?.total) || 0), 0);

    return {
        tours: toursPayload,
        totals: {
            order_total: orderTotal,
        },
    };
};
